/*
 * THE HOUSE OF JK — Choice-bundle builder
 * Ported from JSX lines 753-815 (BundleBuilderModal) + 2435-2438 (confirmBundle).
 *
 * Drives snippets/bundle-builder-modal.liquid, opened by the
 * [data-bundle-open] CTA that sections/mini-bundles.liquid renders on every
 * product whose jk.bundle_type metafield is "choice".
 *
 * Read from the CTA:  data-bundle-count, data-bundle-id, data-bundle-title,
 *                     data-bundle-price
 * Read from the page: <script type="application/json" data-bundle-catalog>
 *                     -> [{ title, handle, gender }]
 *
 * ---------------------------------------------------------------------------
 * CART CONTRACT — reconciled against the shipped assets/theme.js.
 *
 * The brief proposed a JSON POST plus a 'jk:cart:updated' event for theme.js to
 * listen for. theme.js does NOT listen for that event; it owns a delegated
 * `submit` handler for every [data-add-to-cart] form, and its own comment names
 * the bundle builder as one of them ("present or injected later"). The snippet
 * ships without that hook, so this file adds it at runtime.
 *
 * Primary path — the one that runs in the real theme:
 *   1. confirm submits [data-bundle-form] natively.
 *   2. A capture-phase listener writes the variant id and the joined picks into
 *      the hidden inputs (name="id", name="properties[Selected Scents]") first.
 *   3. theme.js's document listener cancels the event and POSTs the FormData to
 *      routes.cart_add_url — giving toast, spray-puff, cart count, drawer.
 *   4. A window-level bubble listener sees defaultPrevented, closes the modal
 *      and dispatches 'jk:cart:updated' on document (informational; nothing is
 *      required to consume it).
 *
 * Fallback — only if no cart JS cancelled the submit:
 *   this file POSTs { id, quantity: 1, properties: { "Selected Scents": "A, B, C" } }
 *   as JSON to window.routes.cart_add_url itself, then closes and announces.
 *   On failure it dispatches 'jk:cart:error' with { detail: { message } }.
 *
 * Either way the payload is identical and properties[Selected Scents] survives,
 * which is what SPEC section 3 requires.
 * ---------------------------------------------------------------------------
 *
 * No English is written here — every string comes from Liquid, either as text
 * already in the modal or as a __COUNT__ / __PRICE__ template attribute.
 */
(function () {
  'use strict';

  // The line item property SPEC section 3 mandates; also the hidden input's
  // name in bundle-builder-modal.liquid: properties[Selected Scents].
  var PROPERTY_NAME = 'Selected Scents';

  /* ---------------------------------------------------------------- utils */

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function attr(el, name) {
    if (!el) return '';
    var v = el.getAttribute(name);
    return v == null ? '' : v;
  }

  function isRtl() {
    return !!(window.jkSettings && window.jkSettings.isRtl);
  }

  // JSX 2436: the picks are joined with ", " in English and "، " in Arabic.
  function joinPicks(titles) {
    return titles.join(isRtl() ? '، ' : ', ');
  }

  /*
   * jk.gender is "For Him" / "For Her" / "Uni Sex". Anything else — including a
   * blank metafield on a fresh store — lands in the unisex column rather than
   * vanishing, so a shopper can always complete a bundle.
   */
  function groupFor(gender) {
    var key = String(gender == null ? '' : gender).toLowerCase().replace(/[^a-z]/g, '');
    if (key === 'forhim' || key === 'him' || key === 'men' || key === 'male') return 'for-him';
    if (key === 'forher' || key === 'her' || key === 'women' || key === 'female') return 'for-her';
    return 'unisex';
  }

  function readCatalog() {
    var node = document.querySelector('script[type="application/json"][data-bundle-catalog]');
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent || '[]');
      if (!Array.isArray(data)) return [];
      var out = [];
      for (var i = 0; i < data.length; i++) {
        if (data[i] && data[i].title) out.push(data[i]);
      }
      return out;
    } catch (e) {
      return [];
    }
  }

  /* -------------------------------------------------------------- elements */

  var overlay, modal, titleEl, introEl, counterEl, ctaEl, formEl, variantEl, propsEl;
  var grids = {};

  function cacheElements() {
    overlay = document.querySelector('[data-bundle-modal-overlay]');
    if (!overlay) return false;
    modal = overlay.querySelector('[data-bundle-modal]');
    titleEl = overlay.querySelector('[data-bundle-title]');
    introEl = overlay.querySelector('[data-bundle-intro]');
    counterEl = overlay.querySelector('[data-bundle-counter]');
    ctaEl = overlay.querySelector('[data-bundle-cta]');
    formEl = overlay.querySelector('[data-bundle-form]');
    variantEl = overlay.querySelector('[data-bundle-variant]');
    propsEl = overlay.querySelector('[data-bundle-properties]');

    // theme.js intercepts every [data-add-to-cart] form and its own comment
    // names the bundle builder as one of them, but the snippet ships without
    // the hook. Adding it at runtime (never to the file — that snippet is not
    // mine) routes this form through the one proven add-to-cart path, so the
    // toast, spray-puff, cart count and drawer all behave exactly as they do
    // for a product card.
    if (formEl && !formEl.hasAttribute('data-add-to-cart')) {
      formEl.setAttribute('data-add-to-cart', '');
    }
    grids = {
      'for-him': overlay.querySelector('[data-bundle-grid="for-him"]'),
      'for-her': overlay.querySelector('[data-bundle-grid="for-her"]'),
      'unisex': overlay.querySelector('[data-bundle-grid="unisex"]')
    };
    return true;
  }

  /* ----------------------------------------------------------------- state */

  var open = false;
  var count = 0;        // how many scents this bundle takes
  var variantId = '';
  var price = '';
  var selected = [];    // array of titles, in pick order — JSX 754
  var opener = null;    // for focus restoration
  var submitting = false;
  // True only while THIS modal holds theme.js's scroll lock + focus trap, so the
  // release is never unbalanced (the lock is refcounted and shared with the cart
  // drawer and the index panel).
  var chromeHeld = false;

  /* ---------------------------------------------------------------- render */

  function renderChips() {
    var catalog = readCatalog();
    var buckets = { 'for-him': '', 'for-her': '', 'unisex': '' };

    for (var i = 0; i < catalog.length; i++) {
      var item = catalog[i];
      var title = item.title;
      var checked = selected.indexOf(title) !== -1;
      // JSX 776: everything unpicked goes disabled once the cap is reached.
      var disabled = !checked && selected.length >= count;
      buckets[groupFor(item.gender)] +=
        '<button type="button" class="bundle-chip' + (checked ? ' checked' : '') +
        '" data-bundle-chip data-title="' + esc(title) + '" aria-pressed="' +
        (checked ? 'true' : 'false') + '"' + (disabled ? ' disabled' : '') + '>' +
        esc(title) + '</button>';
    }

    var keys = ['for-him', 'for-her', 'unisex'];
    for (var k = 0; k < keys.length; k++) {
      var grid = grids[keys[k]];
      if (!grid) continue;
      grid.innerHTML = buckets[keys[k]];
      // Not in the source (its three arrays were never empty): on a fresh store
      // a gender group can have no products, and an orphan heading looks broken.
      var group = overlay.querySelector('[data-bundle-group="' + keys[k] + '"]');
      if (group) group.hidden = !buckets[keys[k]];
    }
  }

  function renderCounter() {
    if (counterEl) counterEl.textContent = selected.length + ' / ' + count; // JSX 774
    if (ctaEl) {
      // JSX 780: confirm unlocks only on an exact match, never on "at least".
      ctaEl.disabled = submitting || selected.length !== count;
    }
  }

  function renderAll() {
    renderChips();
    renderCounter();
  }

  /* ------------------------------------------------------------ open/close */

  function openModal(button) {
    if (!overlay) return;

    var rawCount = parseInt(attr(button, 'data-bundle-count'), 10);
    count = isNaN(rawCount) || rawCount < 1 ? 0 : rawCount;
    variantId = attr(button, 'data-bundle-id');
    price = attr(button, 'data-bundle-price');
    selected = [];
    submitting = false;
    opener = button;

    if (!count || !variantId) return; // misconfigured product — stay silent

    if (titleEl) titleEl.textContent = attr(button, 'data-bundle-title'); // JSX 772
    if (introEl) {
      var introTemplate = attr(introEl, 'data-intro-template');
      introEl.textContent = introTemplate
        ? introTemplate.split('__COUNT__').join(String(count))
        : '';
    }
    if (ctaEl) {
      var ctaTemplate = attr(ctaEl, 'data-cta-template');
      ctaEl.textContent = ctaTemplate ? ctaTemplate.split('__PRICE__').join(price) : '';
    }
    if (variantEl) variantEl.value = variantId;
    if (propsEl) propsEl.value = '';

    renderAll();

    open = true;
    overlay.style.display = '';

    // The overlay is display:flex over the whole viewport: without these the
    // shopper can tab straight out of the dialog into the product cards and the
    // header behind it, and the page scrolls under the backdrop on mobile.
    if (window.JK && (window.JK.lockScroll || window.JK.trapFocus)) {
      chromeHeld = true;
      if (window.JK.lockScroll) window.JK.lockScroll();
      if (window.JK.trapFocus) window.JK.trapFocus(modal, button);
    }

    var close = overlay.querySelector('[data-bundle-close]');
    if (close) {
      try { close.focus({ preventScroll: true }); } catch (e) { close.focus(); }
    }
  }

  // Exactly mirrors what openModal took, and only if it took it. Safe to call
  // more than once; a second call is a no-op rather than a stray unlock that
  // would free the scroll for whatever else holds it.
  function releaseChrome() {
    if (!chromeHeld) return;
    chromeHeld = false;
    if (window.JK && window.JK.unlockScroll) window.JK.unlockScroll();
    // false: the opener restore below is ours to do, and double-restoring would
    // fight it.
    if (window.JK && window.JK.releaseFocus) window.JK.releaseFocus(false);
  }

  function closeModal() {
    if (!overlay) return;
    open = false;
    overlay.style.display = 'none';
    releaseChrome();
    if (opener && document.contains(opener)) {
      try { opener.focus({ preventScroll: true }); } catch (e) { opener.focus(); }
    }
    opener = null;
  }

  /* ---------------------------------------------------------------- toggle */

  // JSX 756-763 — verbatim semantics: already-picked removes; at the cap the
  // click is IGNORED (no error, no notice); otherwise it appends.
  function toggleScent(title) {
    if (!title) return;
    var at = selected.indexOf(title);
    if (at !== -1) {
      selected.splice(at, 1);
    } else if (selected.length >= count) {
      return;
    } else {
      selected.push(title);
    }
    renderAll();
  }

  /* --------------------------------------------------------------- confirm */

  function currentProperties() {
    var out = {};
    out[PROPERTY_NAME] = joinPicks(selected);
    return out;
  }

  function announce(properties, payload) {
    document.dispatchEvent(new CustomEvent('jk:cart:updated', {
      bubbles: true,
      detail: {
        source: 'bundle-builder',
        variantId: variantId,
        quantity: 1,
        properties: properties,
        item: payload || null
      }
    }));
  }

  /*
   * Fallback only. theme.js owns the add-to-cart round trip for every
   * [data-add-to-cart] form, and this modal's form is marked as one (see
   * cacheElements), so in the shipped theme the native submit is intercepted
   * there and this never runs. It exists so the bundle still reaches the cart
   * if theme.js is absent, errored, or replaced.
   */
  function postDirectly(properties) {
    var url = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var numericId = parseInt(variantId, 10);

    submitting = true;
    renderCounter();

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: isNaN(numericId) ? variantId : numericId,
        quantity: 1,
        properties: properties
      })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error((data && data.description) || (data && data.message) || '');
          return data;
        });
      })
      .then(function (data) {
        submitting = false;
        closeModal();
        announce(properties, data);
      })
      .catch(function (error) {
        submitting = false;
        renderCounter();
        document.dispatchEvent(new CustomEvent('jk:cart:error', {
          bubbles: true,
          detail: { source: 'bundle-builder', message: (error && error.message) || '' }
        }));
      });
  }

  /* ------------------------------------------------------------------ wire */

  function onClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var openBtn = target.closest('[data-bundle-open]');
    if (openBtn) {
      event.preventDefault();
      if (!overlay && !cacheElements()) return;
      openModal(openBtn);
      return;
    }

    if (!overlay || !open) return;

    var chip = target.closest('[data-bundle-chip]');
    if (chip && overlay.contains(chip)) {
      event.preventDefault();
      toggleScent(attr(chip, 'data-title'));
      return;
    }

    if (target.closest('[data-bundle-close]')) {
      event.preventDefault();
      closeModal();
      return;
    }

    // JSX 771: the backdrop dismisses; JSX 772 stops the click inside the card.
    if (target === overlay) closeModal();
  }

  /*
   * Capture phase — runs before theme.js's delegated [data-add-to-cart] handler
   * on document. Its only job is to make the form truthful: the variant id and
   * the joined picks must be in the hidden inputs before anyone reads FormData.
   * It never preventDefaults a valid submit, so the real handler still gets it.
   */
  function onSubmitCapture(event) {
    if (!formEl || event.target !== formEl) return;

    // JSX 806: nothing leaves the modal below N. preventDefault alone is not
    // enough — theme.js's handler does not check defaultPrevented, so an
    // incomplete bundle would still be added without its properties. Stop the
    // event dead in capture so no downstream listener ever sees it.
    if (submitting || selected.length !== count || !variantId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (variantEl) variantEl.value = variantId;
    if (propsEl) propsEl.value = joinPicks(selected);
  }

  /*
   * Bubble phase on window — the very last stop, after theme.js's document
   * listener. If the event was cancelled, theme.js took ownership of the round
   * trip: close the modal and announce. If nothing cancelled it, no cart JS is
   * listening, so fall back to posting the payload ourselves.
   */
  function onSubmitFinal(event) {
    if (!formEl || event.target !== formEl) return;
    if (submitting || selected.length !== count || !variantId) return;

    var properties = currentProperties();

    if (event.defaultPrevented) {
      closeModal();
      announce(properties, null);
      return;
    }

    event.preventDefault();
    postDirectly(properties);
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && open) closeModal();
  }

  document.addEventListener('click', onClick);
  document.addEventListener('submit', onSubmitCapture, true);
  window.addEventListener('submit', onSubmitFinal);
  document.addEventListener('keydown', onKeydown);

  // Idempotent: only re-reads the DOM. The overlay ships hidden from Liquid, so
  // booting twice must not close a modal the shopper just opened.
  function boot() {
    cacheElements();
  }

  document.addEventListener('shopify:section:load', function () {
    // mini-bundles.liquid renders the modal, so a re-render replaces these nodes.
    // Hand back the lock/trap first, or they would be stranded on a detached
    // modal and the theme editor would be left unable to scroll.
    releaseChrome();
    open = false;
    cacheElements();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
